import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { FullProject, taskStatusesConfig } from 'entities';

import { Row, Column, Tag, Button, Dot, ScrollArea } from 'components/Blocks';
import { UserAvatarGroup } from 'components/UserAvatar';

import { ServiceOutsourcedTag } from 'pages/Projects/components';

import { useFormControl } from 'hooks';
import { useProjectProfile } from '../context';

import { formatDate } from 'utils/date';

export function KanbanView() {
  const {
    project: { tasks },
    openTask,
  } = useProjectProfile();

  const { state: hiddenControl, dispatch } = useFormControl<Record<string, boolean>>({});

  const { t } = useLang();

  const tasksByCode = useMemo(() => {
    return tasks.reduce(
      (acc, task) => ({
        ...acc,
        [task.status]: [...(acc[task.status] || []), task],
      }),
      {} as Record<string, FullProject['tasks']>,
    );
  }, [tasks]);

  const statusRenderer = useMemo(() => {
    return Object.values(taskStatusesConfig)
      .sort((a, b) => a.order - b.order)
      .filter(({ onlyShowOnKanbanIfPresent, code }) =>
        onlyShowOnKanbanIfPresent ? tasksByCode[code]?.length : true,
      )
      .sort(
        (a, b) => Number(hiddenControl[b.code] ?? false) - Number(hiddenControl[a.code] ?? false),
      );
  }, [tasksByCode, hiddenControl]);

  const shownStatusList = statusRenderer.map(({ code }) => !hiddenControl[code]);

  const canHide = shownStatusList.filter(Boolean).length > 1;

  return (
    <ScrollArea orientation="horizontal">
      <div
        className="grid items-start gap-2"
        style={{
          gridTemplateColumns: shownStatusList
            .map((show) => (show ? 'minmax(190px, 1fr)' : '40px'))
            .join(' '),
        }}
      >
        {statusRenderer.map(({ getLabel, color, code }) => (
          <Column key={`${code}-${getLabel(t)}`} className="h-full rounded-lg bg-muted p-2">
            <div className="sticky top-0 z-10 flex items-center justify-center bg-muted">
              <Button
                variant="ghost"
                className="w-full text-foreground"
                tooltipContent={hiddenControl[code] && getLabel(t)}
                onClick={() => dispatch({ [code]: canHide ? !hiddenControl[code] : false })}
              >
                <Dot color={color} />

                {!hiddenControl[code] && <span>{getLabel(t)}</span>}
              </Button>
            </div>

            {hiddenControl[code] ? (
              <span className="text-center">{tasksByCode[code]?.length || ''}</span>
            ) : (
              <>
                {tasksByCode[code]?.map(
                  ({ name, baseName, tags, expectedDate, id, owner, subOwner, outsourced }) => (
                    <button
                      className="rounded-lg bg-background p-2 text-center text-sm font-medium transition-colors hover:bg-hover"
                      onClick={() => openTask(id)}
                      key={`kanban-${id}`}
                    >
                      <Column gap="2">
                        <span>{name}</span>

                        {name.toLowerCase() !== baseName.toLowerCase() && <span>{baseName}</span>}

                        {(!!tags.length || outsourced) && (
                          <Row gap="2" justifyContent="center" flexWrap="wrap">
                            {outsourced && <ServiceOutsourcedTag />}

                            {tags.map(({ tag }) => (
                              <Tag key={`${id}-${tag.id}`} {...tag} size="medium" />
                            ))}
                          </Row>
                        )}

                        <Row gap="2" justifyContent="space-between" fullWidth>
                          <span className="text-xs">
                            {expectedDate ? formatDate(expectedDate) : t('no-date-given')}
                          </span>

                          <UserAvatarGroup
                            size={24}
                            users={[owner, subOwner]}
                            avatarClassName="text-xs"
                          />
                        </Row>
                      </Column>
                    </button>
                  ),
                )}
              </>
            )}
          </Column>
        ))}
      </div>
    </ScrollArea>
  );
}
